if (customElements.get('bundle-card-picker')) {
  // Already defined; avoid double-registration on section re-render
} else {
  class BundleCardPicker extends HTMLElement {
    connectedCallback() {
      this._initData();
      this._initElements();
      this._hideNativePicker();
      this._bindCardEvents();
      this._bindNavigation();
      this._bindInfoButton();
      this._watchVariantChanges();

      const initial = this._findVariant(this.currentVariantId);
      if (initial) {
        this._lastSeenVariantId = String(initial.id);
        this._refreshUI(initial, { scroll: false, smooth: false });
      }

      requestAnimationFrame(() => this._updateNavVisibility());
    }

    disconnectedCallback() {
      if (this._urlPollId) clearInterval(this._urlPollId);
      if (this._formInputPollId) clearInterval(this._formInputPollId);
      if (this._domPollId) clearInterval(this._domPollId);
      if (this._clickGuardTimer) clearTimeout(this._clickGuardTimer);
      if (this._formObserver) this._formObserver.disconnect();
      if (this._resizeObserver) this._resizeObserver.disconnect();
      if (this._gridScrollHandler && this.gridEl) {
        this.gridEl.removeEventListener('scroll', this._gridScrollHandler);
      }
      if (this._infoOutsideClick) document.removeEventListener('click', this._infoOutsideClick);
      if (this._infoKeydown) document.removeEventListener('keydown', this._infoKeydown);
      if (this._onVariantChangeBound) {
        document.removeEventListener('on:variant:change', this._onVariantChangeBound);
      }
    }

    // ───── Init ─────

    _initData() {
      this.optionName = this.dataset.optionName || '';
      this.optionIndex0 = parseInt(this.dataset.optionIndex0 || '0', 10);
      this.optionPosition = this.dataset.optionPosition || '';
      this.productUrl = this.dataset.productUrl || '';
      this.currentVariantId = this.dataset.currentVariantId || '';
      this.variants = this._parseVariantsData();
      this._lastSeenVariantId = this.currentVariantId;
    }

    _initElements() {
      this.gridEl = this.querySelector('.bundle-card-picker__grid');
      this.cards = Array.from(this.querySelectorAll('.bundle-card-picker__card'));
      this.prevBtn = this.querySelector('.bundle-card-picker__nav--prev');
      this.nextBtn = this.querySelector('.bundle-card-picker__nav--next');
      this.infoTrigger = this.querySelector('.bundle-card-picker__info-trigger');
      this.infoContent = this.querySelector('.bundle-card-picker__info-content');
      this.matchingOptionSelector = this._findMatchingOptionSelector();
    }

    _parseVariantsData() {
      const dataEl = this.querySelector('.bundle-card-picker__variants-data');
      if (!dataEl) return [];
      try { return JSON.parse(dataEl.textContent.trim()); } catch (e) { return []; }
    }

    // Always re-query: variant-picker may be replaced when variant-picker block
    // is inside a [data-dynamic-variant-content] container that updateContent swaps.
    get variantPicker() {
      return this._findVariantPicker();
    }

    _findVariantPicker() {
      const sectionId = this.dataset.sectionId;
      const all = document.querySelectorAll('variant-picker');
      for (const vp of all) {
        if (!sectionId || vp.dataset.sectionId === sectionId) return vp;
      }
      return null;
    }

    _findMatchingOptionSelector() {
      if (!this.variantPicker) return null;
      if (this.optionPosition) {
        const byIndex = this.variantPicker.querySelector(`[data-index="${this.optionPosition}"]`);
        if (byIndex) return byIndex;
      }
      if (this.optionName) {
        const all = this.variantPicker.querySelectorAll('[data-option]');
        for (const sel of all) {
          if (sel.dataset.option === this.optionName) return sel;
        }
        const lower = this.optionName.toLowerCase().trim();
        for (const sel of all) {
          if ((sel.dataset.option || '').toLowerCase().trim() === lower) return sel;
        }
      }
      return null;
    }

    _hideNativePicker() {
      if (!this.matchingOptionSelector) return;
      this.matchingOptionSelector.classList.add('bundle-aware-hidden');
      this.matchingOptionSelector.setAttribute(
        'style',
        'position:absolute!important;left:-9999px!important;top:auto!important;width:1px!important;height:1px!important;overflow:hidden!important;opacity:0!important;pointer-events:none!important;margin:0!important;padding:0!important;'
      );
    }

    // ───── Events ─────

    _bindCardEvents() {
      this.cards.forEach((card) => {
        card.addEventListener('click', this._onCardClick.bind(this));
      });
    }

    _bindNavigation() {
      if (this.prevBtn) this.prevBtn.addEventListener('click', () => this._scrollByDirection(-1));
      if (this.nextBtn) this.nextBtn.addEventListener('click', () => this._scrollByDirection(1));
      if (this.gridEl) {
        this._gridScrollHandler = () => this._updateNavVisibility();
        this.gridEl.addEventListener('scroll', this._gridScrollHandler, { passive: true });
        if (typeof ResizeObserver !== 'undefined') {
          this._resizeObserver = new ResizeObserver(() => this._updateNavVisibility());
          this._resizeObserver.observe(this.gridEl);
        }
      }
    }

    _watchVariantChanges() {
      const onExternalChange = (variantId) => {
        if (this._clickGuard) return;
        if (!variantId) return;
        if (String(variantId) === String(this._lastSeenVariantId)) return;
        const variant = this._findVariant(variantId);
        if (!variant) return;
        this._lastSeenVariantId = String(variantId);
        this._refreshUI(variant, { scroll: true, smooth: true });
        // variant-picker may have been replaced; re-hide the native option in
        // its new DOM home and refresh cached selector reference.
        this.matchingOptionSelector = this._findMatchingOptionSelector();
        this._hideNativePicker();
      };

      // Document-level listener survives variant-picker replacement (the picker
      // may be inside a [data-dynamic-variant-content] container that gets swapped).
      this._onVariantChangeBound = (e) => {
        const targetPicker = e.target && e.target.closest && e.target.closest('variant-picker');
        if (!targetPicker) return;
        const ourSectionId = this.dataset.sectionId;
        const targetSectionId = targetPicker.dataset.sectionId;
        if (ourSectionId && targetSectionId && ourSectionId !== targetSectionId) return;
        const v = e.detail && e.detail.variant;
        if (v) onExternalChange(v.id);
      };
      document.addEventListener('on:variant:change', this._onVariantChangeBound);

      this._urlPollId = setInterval(() => {
        try {
          const id = new URLSearchParams(window.location.search).get('variant');
          onExternalChange(id);
        } catch (e) {}
      }, 600);

      const form = document.querySelector('form[action*="/cart/add"]');
      if (form) {
        const idInput = form.querySelector('input[name="id"]');
        if (idInput) {
          this._formObserver = new MutationObserver(() => onExternalChange(idInput.value));
          this._formObserver.observe(idInput, { attributes: true, attributeFilter: ['value'] });

          this._formInputPollId = setInterval(() => {
            onExternalChange(idInput.value);
          }, 350);
        }
      }

      this._domPollId = setInterval(() => {
        const variant = this._resolveCurrentVariantFromDom();
        if (variant) onExternalChange(variant.id);
      }, 300);
    }

    _resolveCurrentVariantFromDom() {
      if (!this.variantPicker) return null;
      const selectors = this.variantPicker.querySelectorAll('[data-option]');
      if (!selectors.length) return null;

      const optionValues = [];
      selectors.forEach((sel) => {
        const pos = parseInt(sel.dataset.index || '0', 10);
        if (!pos) return;
        const val = this._getSelectedValueFromSelector(sel);
        if (val) optionValues[pos - 1] = val;
      });

      if (!optionValues.length) return null;

      return (
        this.variants.find((v) => {
          if (!v.options) return false;
          for (let i = 0; i < v.options.length; i++) {
            if (optionValues[i] !== undefined && v.options[i] !== optionValues[i]) {
              return false;
            }
          }
          return true;
        }) || null
      );
    }

    _getSelectedValueFromSelector(sel) {
      const checked = sel.querySelector('input[type="radio"]:checked');
      if (checked && checked.value) return checked.value;

      const selectedOption = sel.querySelector('.custom-select__option[aria-selected="true"]');
      if (selectedOption && selectedOption.dataset.value) return selectedOption.dataset.value;

      const nativeSelect = sel.querySelector('select');
      if (nativeSelect && nativeSelect.value) return nativeSelect.value;

      const labelValue = sel.querySelector('.option-selector__label-value');
      if (labelValue && labelValue.textContent && labelValue.textContent.trim()) {
        return labelValue.textContent.trim();
      }

      return null;
    }

    _onCardClick(event) {
      const card = event.currentTarget;
      if (card.classList.contains('is-unavailable')) {
        event.preventDefault();
        return;
      }
      const value = card.dataset.value;

      const currentVariant = this._findVariant(this._lastSeenVariantId)
        || this._findVariant(this.currentVariantId)
        || (this.variants.length ? this.variants[0] : null);
      if (!currentVariant) return;

      const targetVariant = this._findVariantForCard(value, currentVariant, true);
      if (!targetVariant) return;

      if (window.__bundleDebug === true) {
        console.log('[bundle] _onCardClick', {
          value,
          fromVariantId: currentVariant.id,
          toVariantId: targetVariant.id,
        });
      }

      this._lastSeenVariantId = String(targetVariant.id);
      this._refreshUI(targetVariant, { scroll: false, smooth: true });
      this._updateFormVariantId(targetVariant.id);

      this._clickGuard = true;
      if (this._clickGuardTimer) clearTimeout(this._clickGuardTimer);
      this._clickGuardTimer = setTimeout(() => {
        this._clickGuard = false;
      }, 1200);

      const dispatched = this._triggerNativeChange(value);
      if (!dispatched) {
        this._navigateToVariant(targetVariant.id);
      }
    }

    // ───── UI Refresh ─────

    _refreshUI(currentVariant, { scroll = true, smooth = true } = {}) {
      let selectedChanged = false;

      this.cards.forEach((card) => {
        const value = card.dataset.value;
        const wasSelected = card.classList.contains('is-selected');
        const exactMatch = this._findVariantForCard(value, currentVariant, true);
        const display = exactMatch || this._findVariantForCard(value, currentVariant, false);

        if (display) {
          const isSelected = !!exactMatch && value === currentVariant.options[this.optionIndex0];
          const available = !!(exactMatch && exactMatch.available);
          const imageSrc = this._resolveCardImage(exactMatch, currentVariant, display);
          this._updateCard(card, display, isSelected, available, imageSrc);
          if (isSelected !== wasSelected) selectedChanged = true;
        } else {
          if (wasSelected) selectedChanged = true;
          this._markUnavailable(card);
        }
      });

      this._updateNavVisibility();

      if (scroll && selectedChanged) {
        this._scrollSelectedIntoView(smooth);
      }
    }

    _resolveCardImage(exactMatch, currentVariant, fallback) {
      if (exactMatch && exactMatch.image_src) return exactMatch.image_src;

      if (currentVariant) {
        for (const v of this.variants) {
          let allOther = true;
          for (let i = 0; i < (v.options || []).length; i++) {
            if (i === this.optionIndex0) continue;
            if (v.options[i] !== currentVariant.options[i]) {
              allOther = false;
              break;
            }
          }
          if (allOther && v.image_src) return v.image_src;
        }
      }

      if (fallback && fallback.image_src) return fallback.image_src;
      return null;
    }

    _findVariant(id) {
      if (!id) return null;
      return this.variants.find((v) => String(v.id) === String(id)) || null;
    }

    _findVariantForCard(cardValue, currentVariant, requireExact = false) {
      let exact = null;
      let fallback = null;
      for (const v of this.variants) {
        if (!v.options || v.options[this.optionIndex0] !== cardValue) continue;
        if (!fallback) fallback = v;
        let allOtherMatch = true;
        for (let i = 0; i < v.options.length; i++) {
          if (i === this.optionIndex0) continue;
          if (v.options[i] !== currentVariant.options[i]) {
            allOtherMatch = false;
            break;
          }
        }
        if (allOtherMatch) {
          exact = v;
          break;
        }
      }
      if (requireExact) return exact;
      return exact || fallback;
    }

    _updateCard(card, variant, isSelected, available = null, imageSrcOverride = null) {
      const img = card.querySelector('.bundle-card-picker__media img');
      const imageSrc = imageSrcOverride !== null ? imageSrcOverride : variant.image_src;
      if (img && imageSrc) {
        const newSrc = this._imageUrlAt(imageSrc, 400);
        if (img.getAttribute('src') !== newSrc) {
          img.src = newSrc;
          img.srcset = [200, 400, 600]
            .map((w) => `${this._imageUrlAt(imageSrc, w)} ${w}w`)
            .join(', ');
        }
        if (variant.image_alt && img.alt !== variant.image_alt) {
          img.alt = variant.image_alt;
        }
      }

      const isAvailable = available !== null ? available : !!variant.available;

      const priceEl = card.querySelector('.bundle-card-picker__price');
      if (priceEl) {
        priceEl.textContent = isAvailable ? variant.price_formatted : 'Stokta yok';
      }

      card.dataset.variantId = String(variant.id);

      const input = card.querySelector('.bundle-card-picker__input');
      const unavailable = !isAvailable;

      card.classList.toggle('is-unavailable', unavailable);
      card.classList.toggle('is-selected', isSelected);

      if (input) {
        input.disabled = unavailable;
        if (unavailable) input.setAttribute('aria-disabled', 'true');
        else input.removeAttribute('aria-disabled');
        input.checked = isSelected;
      }
    }

    _markUnavailable(card) {
      card.classList.add('is-unavailable');
      card.classList.remove('is-selected');
      const priceEl = card.querySelector('.bundle-card-picker__price');
      if (priceEl) priceEl.textContent = 'Stokta yok';
      const input = card.querySelector('.bundle-card-picker__input');
      if (input) {
        input.disabled = true;
        input.setAttribute('aria-disabled', 'true');
        input.checked = false;
      }
    }

    _imageUrlAt(src, width) {
      if (!src) return src;
      try {
        const url = new URL(src, window.location.origin);
        url.searchParams.set('width', String(width));
        return url.toString();
      } catch (e) {
        return src;
      }
    }

    // ───── Variant change side effects ─────

    _updateFormVariantId(variantId) {
      if (!variantId) return;
      const forms = document.querySelectorAll('form[action*="/cart/add"]');
      forms.forEach((form) => {
        const input = form.querySelector('input[name="id"]');
        if (input && input.value !== String(variantId)) {
          input.value = String(variantId);
          input.dispatchEvent(new Event('change', { bubbles: true }));
        }
      });
    }

    _triggerNativeChange(value) {
      this.matchingOptionSelector = this._findMatchingOptionSelector();
      if (!this.matchingOptionSelector) return false;
      const root = this.matchingOptionSelector;
      const escaped = CSS.escape(value);
      const debug = window.__bundleDebug === true;

      const customOption = root.querySelector(
        `custom-select .custom-select__option[data-value="${escaped}"]:not([aria-selected="true"])`
      );
      if (customOption) {
        const customSelect = customOption.closest('custom-select');
        if (customSelect && typeof customSelect.selectOption === 'function') {
          const cleanDetail = {
            noFocus: true,
            selectedValue: customOption.dataset.value,
            productUrl: customOption.dataset.productUrl,
            variantId: customOption.dataset.variantId,
          };

          const swallow = (evt) => {
            evt.stopImmediatePropagation();
            customSelect.dispatchEvent(new CustomEvent('change', {
              bubbles: true,
              cancelable: false,
              detail: cleanDetail,
            }));
          };
          customSelect.addEventListener('change', swallow, { capture: true, once: true });

          const nativeSelect = customSelect.querySelector('select');
          if (nativeSelect) {
            nativeSelect.addEventListener(
              'change',
              (evt) => evt.stopImmediatePropagation(),
              { capture: true, once: true }
            );
          }

          if (debug) console.log('[bundle] _triggerNativeChange dropdown', { value, variantId: cleanDetail.variantId });
          customSelect.selectOption(customOption);
          return true;
        }
      }

      const radio = root.querySelector(`input[type="radio"][value="${escaped}"]:not(:checked)`);
      if (radio) {
        const labelValue = root.querySelector('.option-selector__label-value');
        if (labelValue) labelValue.textContent = value;
        radio.checked = true;
        if (debug) console.log('[bundle] _triggerNativeChange radio', { value });
        radio.dispatchEvent(
          new CustomEvent('change', {
            bubbles: true,
            cancelable: false,
            detail: { noFocus: true },
          })
        );
        return true;
      }

      const select = root.querySelector('select');
      if (select) {
        for (const opt of select.options) {
          if (opt.value === value) {
            select.value = value;
            if (debug) console.log('[bundle] _triggerNativeChange native-select', { value });
            select.dispatchEvent(new CustomEvent('change', {
              bubbles: true,
              cancelable: false,
              detail: { noFocus: true },
            }));
            return true;
          }
        }
      }

      return false;
    }

    _navigateToVariant(variantId) {
      const url = new URL(this.productUrl || window.location.pathname, window.location.origin);
      url.searchParams.set('variant', String(variantId));
      window.location.href = url.toString();
    }

    // ───── Carousel navigation ─────

    _scrollByDirection(direction) {
      if (!this.gridEl) return;
      const cardWidth = this.cards[0] ? this.cards[0].offsetWidth : 200;
      this.gridEl.scrollBy({
        left: direction * (cardWidth + 10),
        behavior: 'smooth',
      });
    }

    _updateNavVisibility() {
      if (!this.gridEl || !this.prevBtn || !this.nextBtn) return;
      const { scrollLeft, scrollWidth, clientWidth } = this.gridEl;
      const tolerance = 4;
      const overflow = scrollWidth - clientWidth > tolerance;

      if (!overflow) {
        this.prevBtn.hidden = true;
        this.nextBtn.hidden = true;
        return;
      }
      this.prevBtn.hidden = scrollLeft <= tolerance;
      this.nextBtn.hidden = scrollLeft + clientWidth >= scrollWidth - tolerance;
    }

    _scrollSelectedIntoView(smooth = true) {
      if (!this.gridEl) return;
      const { scrollWidth, clientWidth, scrollLeft } = this.gridEl;
      if (scrollWidth <= clientWidth + 4) return;

      const selected = this.querySelector('.bundle-card-picker__card.is-selected');
      if (!selected) return;

      const target =
        selected.offsetLeft - (clientWidth - selected.offsetWidth) / 2;
      const clamped = Math.max(0, Math.min(target, scrollWidth - clientWidth));

      if (Math.abs(scrollLeft - clamped) < 16) return;

      this.gridEl.scrollTo({
        left: clamped,
        behavior: smooth ? 'smooth' : 'auto',
      });
    }

    // ───── Info button popover ─────

    _bindInfoButton() {
      if (!this.infoTrigger || !this.infoContent) return;

      this._infoOutsideClick = (e) => {
        if (this.infoContent.contains(e.target) || this.infoTrigger.contains(e.target)) return;
        this._closeInfo();
      };
      this._infoKeydown = (e) => {
        if (e.key === 'Escape') {
          this._closeInfo();
          this.infoTrigger.focus();
        }
      };

      this.infoTrigger.addEventListener('click', (e) => {
        e.stopPropagation();
        const isOpen = this.infoTrigger.getAttribute('aria-expanded') === 'true';
        if (isOpen) this._closeInfo();
        else this._openInfo();
      });
    }

    _openInfo() {
      this.infoTrigger.setAttribute('aria-expanded', 'true');
      this.infoContent.hidden = false;
      document.addEventListener('click', this._infoOutsideClick);
      document.addEventListener('keydown', this._infoKeydown);
    }

    _closeInfo() {
      this.infoTrigger.setAttribute('aria-expanded', 'false');
      this.infoContent.hidden = true;
      document.removeEventListener('click', this._infoOutsideClick);
      document.removeEventListener('keydown', this._infoKeydown);
    }
  }

  customElements.define('bundle-card-picker', BundleCardPicker);
}
